import { of, combineLatest, forkJoin, Subject, merge } from 'rxjs';
import { Component, OnDestroy } from '@angular/core';
import { ItemService, UserService, StreamService, TenantService, UtilService, PermissionService } from '../services'
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import * as _ from 'lodash';
import { filter, tap, mergeMap, takeUntil, map } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { NameTopicDialogComponent } from '../digestReader/nameTopicDialog.component';
import { BasicFilterStructure } from '../services/stream.service';
import { ItemContextModel } from '../model';
import { FormControl, Validators } from '@angular/forms';
import { TagSelectorDialogComponent } from './tagSelectorDialog.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

interface TagSet {
    type: string, 
    tags: any[]
} 

interface TagSetGroup {
    tagSets: TagSet[]
}

@Component({
    selector: 'search-pane',
    styles: [`
        mat-card {
            margin-bottom: 6px;
        }
        
        .title {
            font-size: 20px;
        }

        .itemsPanel {
            overflow-y: auto;
            height: calc(100vh - 191px);
        }
    `],
    template: `
        <div fxFlex="100%" fxLayout="column">
            <mat-card>
                <mat-card-content>
                    <div fxLayout="row">
                        <mat-card-title class="title">Search</mat-card-title>
                        <span fxFlex></span>
                        <mat-select placeholder="Sort By" [(ngModel)]="sort" (selectionChange)="doSearch()" fxFlex="50px" style="margin-right: 20px">
                            <mat-select-trigger>
                                <mat-icon color="primary" [matTooltip]="'Sorted by: ' + sort.label" style="transform: scale(-1, 1);">{{sort.icon}}</mat-icon>
                            </mat-select-trigger>
                            <mat-option *ngFor="let sortOption of sortOptions" [value]="sortOption">
                                <mat-icon style="transform: scale(-1, 1);">{{sortOption.icon}}</mat-icon>{{sortOption.label}}
                            </mat-option>
                        </mat-select>
                        <mat-slide-toggle [(ngModel)]="restrictToMyTopic" (change)="doSearch()">Only My Topics</mat-slide-toggle>
                    </div>
                    <div fxLayout="row">
                        <mat-form-field fxFlex="40">
                            <input matInput
                                   #searchControlRef 
                                   placeholder="Search Terms" 
                                   [formControl]="searchControl"
                                   (blur)="searchWith({ term: searchControlRef.value })" 
                                   (keyup.enter)="searchWith({ term: searchControlRef.value })"/>
                        </mat-form-field>
                        <span fxFlex></span>
                        <button *ngIf="currentUser && canCreateStreams" mat-raised-button (click)="createTopic()" [disabled]="!streamIds?.length || noTouchingSave" style="height: 35px;">Create Topic from Search</button>
                    </div>
                </mat-card-content>
            </mat-card>
            <div fxLayout="row">
                <div *ngIf="!isPhone" fxLayout="column" fxFlex="400px" fxFlexOffset="5px">
                    <mat-card *ngFor="let tagSetGroup of tagSetGroups; let tsgIndex = index;">
                        <mat-card-content>
                            <div>
                                <div fxLayout="row">
                                    <h4 *ngIf="!tagSetGroup.tagSets?.length">Select tags to refine results</h4>
                                    <h4 *ngIf="tagSetGroup.tagSets?.length && tsgIndex === 0">Include articles with any of these:</h4>
                                    <h4 *ngIf="tagSetGroup.tagSets?.length && tsgIndex !== 0">And any of these:</h4>
                                    <span fxFlex></span>
                                    <span [matTooltip]="availableTags?.length ? 'Show Tags' : 'No Available Tags'">
                                    <button mat-button 
                                            [disabled]="!availableTags?.length || noTouching" 
                                            (click)="addTags(tagSetGroup)"
                                            style="margin-top: 8px; font-weight: bold;">Show Tags</button>
                                    </span>
                                </div>
                                <div *ngIf="tagSetGroup.tagSets?.length" style="padding-top: 0px;">
                                    <div *ngFor="let tagSet of tagSetGroup.tagSets; let tgi = index;">
                                        <div>
                                            <div *ngFor="let tag of tagSet.tags; let i = index;"
                                                    [ngClass]="tagDefs[tag.type]?.cls" style="display: inline-block; line-height: 25px;">
                                                <span *ngIf="i !== 0 || tgi !== 0" style="padding-left: 5px; padding-right: 5px; font-weight: bold;">OR</span>
                                                {{tag.tagText}} {{tagCountLabel(tag)}}
                                                <button mat-icon-button 
                                                        style="text-align: left; line-height: 25px; height: 25px; max-height: 25px; width: 20px;" 
                                                        (click)="removeTag(tagSet, i)"
                                                        [matTooltip]="'Remove ' + tag.tagText">
                                                    <mat-icon style="font-size: 15px;">highlight_off</mat-icon>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </mat-card-content>
                    </mat-card>
                    <mat-card>
                        <mat-card-content>
                            <span fxFlex></span><button mat-button (click)="addTagSetGroup()" [disabled]="!availableTags?.length || noTouching">+ AND</button>
                        </mat-card-content>
                    </mat-card>
                    <mat-card>
                        <mat-card-content>
                            <mat-form-field fxFlex="250px">
                                <input matInput 
                                       #publishedWithinDaysControlRef
                                       placeholder="Only articles published in the last"
                                       [formControl]="publishedWithinDaysControl"
                                       (blur)="searchWith({ publishedWithinDays: publishedWithinDaysControlRef.value })"
                                       (keyup.enter)="searchWith({ publishedWithinDays: publishedWithinDaysControlRef.value })">
                                <span matSuffix>days</span>
                                <mat-error *ngIf="publishedWithinDaysControl.hasError('min')">Published within must be greater than zero.</mat-error>
                            </mat-form-field>
                        </mat-card-content>
                    </mat-card>
                </div>
                <div fxFlex fxLayout="column" fxFlexOffset="5px">
                    <div *ngIf="noTouching" fxLayout="column" fxLayoutAlign="center center" style="min-height:400px;">
                        <spinner></spinner>
                    </div>
                    <div *ngIf="!noTouching && items && !items.length" fxLayout="column" fxLayoutAlign="center center" class="no-results">
                        <h1 class="text-center"><i class="fa fa-search"></i></h1>
                        <div *ngIf="term?.length">No results found for {{term}}.</div>
                        <div *ngIf="!term?.length">Enter search terms to find matching articles</div>
                    </div>
                    <div class="itemsPanel" infiniteScroll [infiniteScrollThrottle]="300" [scrollWindow]="false" (scrolled)="fetchItems(true)">
                        <mat-card *ngIf="!noTouching && items && items.length">
                            <mat-card-content>{{totalElements}} Items</mat-card-content>
                        </mat-card>
                        <span *ngFor="let item of items">
                            <item-summary [item]="item"></item-summary>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    `})
export class SearchPane implements OnDestroy {
    private cleanupTime = new Subject<void>();
    searchControl = new FormControl(undefined, { updateOn: 'blur' });
    publishedWithinDaysControl = new FormControl(undefined, { updateOn: 'blur', validators: [Validators.min(1)] });
    items: any = [];
    sort;
    noTouching: boolean = true;
    restrictToMyTopic: boolean = false;
    itemSearchParams = { page: 0 };
    sortOptions = [
        { label: 'Published Date', key: 'publishedDate', icon: 'today' },
        { label: 'Relevance', key: 'relevance', icon: 'signal_cellular_alt' }
    ];
    streamIds: any[];
    tagSetGroups: TagSetGroup[] = [];
    tagCount = 0;
    tagDefs;
    noTouchingSave: boolean;
    canCreateStreams: boolean;
    availableTags: Attensa.TagSet[];
    isPhone: boolean;
    totalElements: any;

    constructor(private router: Router, 
                private route: ActivatedRoute,
                private dialog: MatDialog, 
                private itemService: ItemService,
                private permissionService: PermissionService,
                private streamService: StreamService,
                private tenantService: TenantService,
                private userService: UserService,
                private utilService: UtilService,
                private breakpointObserver: BreakpointObserver){}

    ngOnInit(): void {
        this.breakpointObserver.observe([Breakpoints.Handset]).pipe(takeUntil(this.cleanupTime)).subscribe(result => this.isPhone = result.matches);

        this.tagDefs = this.itemService.tagDefs;
        this.route.queryParams.pipe(
            tap(() => this.tagSetGroups = []),
            tap(() => this.canCreateStreams = this.permissionService.currentUserCanCreateStream()),
            tap(params => this.searchControl.setValue(params.term, { emitEvent: false })),
            tap(params => {
                if (params.publishedWithinDays?.trim().length) {
                    this.publishedWithinDaysControl.setValue(isNaN(Number(params.publishedWithinDays)) ? undefined : Number(params.publishedWithinDays), { emitEvent: false });
                }
            }),
            tap(params => this.restrictToMyTopic = params.restrictToMyTopic === 'true'),
            tap(params => {
                const selectedSort = params.sort ? _.find(this.sortOptions, option => option.key === params.sort) : this.sortOptions[0];
                this.sort = selectedSort || this.sortOptions[0];    
            }),
            tap(params => this.configureTagSets(params)),
            tap(() => this.noTouching = false),
            filter(params => params.tags || params.term?.length),
            takeUntil(this.cleanupTime)
        ).subscribe(() => this.fetchItems());

        this.route.url.pipe(
            map((segs: UrlSegment[]) => segs.length ? segs[1].path : undefined),
            tap(() => this.itemService.focusedItemEdit.next(undefined)),
            filter(itemId => !!itemId),
            takeUntil(this.cleanupTime)
        ).subscribe((itemId: string) => {
            let item = this.items.find(item => item.id === itemId);
            const curating = false;
            const isSource = false;
                
            if (!item) {
                this.itemService.getItem(itemId)
                .pipe(map(response => response.item))
                .subscribe(fetchedItem => this.itemService.focusedItem.next(new ItemContextModel(fetchedItem, undefined, undefined, undefined, isSource, curating)));
            } else {
                this.itemService.focusedItem.next(new ItemContextModel(item, undefined, undefined, undefined, isSource, curating));
            }
        });
        this.itemService.itemClosing.pipe(takeUntil(this.cleanupTime)).subscribe(() => this.router.navigateByUrl(this.itemService.childClearedPath()));
        this.itemService.itemContextChanged.pipe(takeUntil(this.cleanupTime)).subscribe(() => this.fetchItems());
    }

    configureTagSets(params) {
        if (params.tags) {
            const tagItems = params.tags.split(',');
            this.tagCount = tagItems.length; 
            tagItems.forEach(tagItem => {
                const [tag, type, idx] = tagItem.split('__');
                const groupIdx = idx || 0;
                let tagSetGroup = this.tagSetGroups[groupIdx];
                if (!tagSetGroup) {
                    for(let x = 0; x <= groupIdx; x++) {
                        if (!this.tagSetGroups[x]) {
                            this.tagSetGroups.push({ tagSets: [] });
                        }
                    }
                    tagSetGroup = this.tagSetGroups[groupIdx];
                }
                let tagSet = tagSetGroup.tagSets.find(ts => ts.type === type);
                if (!tagSet) {
                    tagSet = { type, tags: []};
                    tagSetGroup.tagSets.push(tagSet);
                }
                tagSet.tags.push({ tagText: tag, type });
            });
        }
        if (!this.tagSetGroups.length) {
            this.tagSetGroups.push({ tagSets: [] });
        }
    }

    tagGroupLabels(tagSet): string[] {
        return Object.keys(tagSet);
    }

    tagParam(): string {
        const params = this.tagSetGroups.flatMap((tsg, index) => {
            return tsg.tagSets.flatMap(ts => {
                return ts.tags.map(tag => `${tag.tagText}__${tag.type}__${index}`);
            })
        });
        return params?.length ? params.join(',') : undefined;
    }

    removeTag(tagSet, index): void {
        tagSet.tags.splice(index, 1);
        const tagParamVal = this.tagParam();
        if ((this.searchControl.value?.trim().length) || (tagParamVal && tagParamVal.trim().length)) {
            this.doSearch();
        } else {
            this.router.navigate(['search']);
        }
    }

    searchWith(override): void {
        const isChanged = override ? Object.keys(override).reduce((acc, key) => acc || override[key] !== this.route.snapshot.queryParams[key], false) : true;
        if (isChanged) {
            this.doSearch(override);
        }
    }

    doSearch(override?): void {
        this.noTouching = true;
        const queryParams = Object.assign({ 
            term: this.utilService.cleanQuotes(this.searchControl.value), 
            sort: this.sort ? this.sort.key : undefined, 
            restrictToMyTopic: this.restrictToMyTopic,
            publishedWithinDays: this.publishedWithinDaysControl.value || undefined,
            tags: this.tagParam()
        }, override);
        this.router.navigate(['search'], { queryParams });
    }

    get currentUser() {
        return this.userService.getCurrentUser();
    }

    addTagSetGroup() {
        const tagSetGroup = { tagSets: [] };
        this.tagSetGroups.push(tagSetGroup);
        if (this.availableTags?.length) {
            this.addTags(tagSetGroup);
        }
    }

    tagGroupsToFilterStructure() {
        return this.tagSetGroups.map(tgs => {
            return {
                condition: 'CONTAINS',
                type: 'TAG',
                values: tgs.tagSets.flatMap(ts => ts.tags.map(tag => ({ key: tag.tagText, tagText: tag.tagText, type: tag.type })))
            };
        });
    }

    fetchItems(incrementPage = false): void {
        if (incrementPage) {
            this.itemSearchParams.page++;
        } else {
            this.noTouching = true;
            this.items = [];
        }
        (this.restrictToMyTopic ?
        this.userService.searchItems({
            search: this.utilService.cleanQuotes(this.searchControl.value),
            publishedWithinDays: this.publishedWithinDaysControl.value || undefined,
            page: this.itemSearchParams.page,
            includeOpenForReadingFalseStreams: true,
            includeReadItems: true,
            includeMarketplaceStreams: true,
            includeFullDescription: true,
            includeAggregations: true,
            sortField: this.sort.key,
        }) :
        this.itemService.searchItemsWithSettings({
            search: this.utilService.cleanQuotes(this.searchControl.value),
            publishedWithinDays: this.publishedWithinDaysControl.value || undefined,
            searchConfiguration: { filterStructures: this.tagGroupsToFilterStructure() }
        }, {
            page: this.itemSearchParams.page,
            includeMarketplaceStreams: true,
            includeFullDescription: true,
            includeUnpublishedStreams: true,
            includeOpenForReadingFalseStreams: true,
            includeReadItems: true,
            includeAggregations: true,
            sortField: this.sort.key
        }))
        .pipe(
            tap(itemResults => this.streamIds = itemResults.aggregations?.streamIds?.buckets ? itemResults.aggregations.streamIds.buckets.map(aggr => aggr.key) : []),
            tap(itemResults => this.availableTags = itemResults.aggregations?.tagTypes?.type?.buckets || []),
            // decorate the tags with the set key they belong to so as to be usefull outside of the structure
            tap(() => this.availableTags?.forEach(set => set?.tagText?.buckets?.forEach(t => t.type = set.key))),
            tap(itemResults => this.totalElements = itemResults.totalElements),
            map(itemResults => itemResults.content),
            tap(content => content.forEach(item => item.visible = true)),
            tap(() => this.noTouching = false),
        ).subscribe(content => this.items = this.items.concat(content));
    }

    tagCountLabel(displayTag): string {
        const availableTagSet = this.availableTags?.find(ats => ats.key === displayTag.type);
        const availableTag = availableTagSet?.tagText?.buckets.find(at => at.key === displayTag.tagText);
        return availableTag ? ` (${availableTag.doc_count})` : '';
    }

    buildBasicFilterStructures(): BasicFilterStructure[] {
        const hasTags = this.tagSetGroups.reduce((acc, tsg) => acc || !!tsg.tagSets.length, false);
        if (hasTags) {
            return this.tagGroupsToFilterStructure()
        } else {
            return [];
        }
    }

    createTopic(): void {
        this.noTouchingSave = true;
        const matchType = 'IncludeAnyKeywords';
        const basicFilterStructures = this.buildBasicFilterStructures();
        const filterTerm = this.streamService.basicFilterTermFromFilter({
            isAdvanced: false,
            basicFilterText: this.utilService.cleanQuotes(this.searchControl.value || ''),
            matchType: matchType,
            basicFilterStructures
        });
        combineLatest([
            this.dialog.open(NameTopicDialogComponent).afterClosed(),
            this.tenantService.getUnauthTenantInfo()
        ]).pipe(
            mergeMap(([topicName, tenant]) => {
                if (topicName) {
                    this.noTouching = true;
                    return this.streamService.saveStream({
                        title: topicName,
                        type: 'COLLECTION',
                        openForReading: true,
                        published: tenant.settings.autoTopicPublish === 'true',
                        rssEnabled: false,
                        emailPostingEnabled: false,
                        subStreams: this.streamIds.map(streamId => ({ targetStreamId: streamId, ownerStreamId: null}))
                    });
                } else {
                    this.noTouchingSave = false;
                    return of(null); 
                }
            }))
            .pipe(
                filter(topic => !!topic),
                mergeMap(stream => {
                    return forkJoin([
                        of(stream),
                        this.streamService.setSubscription(stream.id, this.userService.getCurrentUser().id, true, false, false),
                        this.streamService.postFilter(stream.id, { 
                            version: '2',
                            isBasic: true,
                            matchType: matchType,
                            publishedWithinDays: this.publishedWithinDaysControl.value || undefined,
                            filterTerm: filterTerm?.trim().length ? filterTerm : undefined,
                            basicFilterText: this.utilService.cleanQuotes(this.searchControl.value),
                            basicFilterStructures
                        })
                    ]);
                })
        ).subscribe(([topic]) => this.router.navigate(['/topics', topic.id, 'content'])), () => this.noTouchingSave = false;
    }

    selectedTagSetTags(tagSetGroup): Attensa.TagSetTag[] {
        const selected = []; 
        tagSetGroup.tagSets.forEach(set => {
            const ats = this.availableTags.find(ats => ats.key === set.type)
            set.tags.forEach(tag => {
                const at = ats?.tagText.buckets.find(at => at.key === tag.tagText)
                if (at) {
                    selected.push(at);
                }
            })
        })
        return selected;
    }

    tagSetForTag(tag, tagSetGroup): { type: string, tags: any[] } {
        let ts = tagSetGroup.tagSets.find(ts => ts.type === tag.type);
        if (!ts) {
            ts = { type: tag.type, tags: [] };
            tagSetGroup.tagSets.push(ts);
        }
        return ts;
    }

    addTags(tagSetGroup): void {
        this.dialog.open(TagSelectorDialogComponent, { data: {
            initiallySelected: this.selectedTagSetTags(tagSetGroup),
            availableTagSets: this.availableTags 
        }, autoFocus: false }).afterClosed()
        .pipe(
            filter(doIt => !!doIt),
            tap(() => tagSetGroup.tagSets.forEach(ts => ts.tags = [])), // clear current
            tap((filterTags: Attensa.TagSetTag[]) => filterTags.forEach(tag => this.tagSetForTag(tag, tagSetGroup).tags.push({ tagText: tag.key, type: tag.type })))
        ).subscribe(() => this.doSearch());
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}
